<template>
  <v-alert :loading="chartReloading" :icon="false" class="body-2 pa-4 mt-2 info-box">
    <template #title>
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-4 info-icon">
          mdi-cart
        </v-icon>
        <h4 class="text-primary">
          Warenkorb
        </h4>
      </div>
    </template>
    <v-row v-if="chartReloading" no-gutters class="ma-6 mt-4">
      <v-col cols="12">
        <v-row no-gutters class="text-center my-12">
          <v-col cols="12">
            <v-icon x-large color="#000">
              mdi-cart-remove
            </v-icon>
          </v-col>
          <v-col cols="12">
            <h4>Warenkorb-Neuberechnung</h4>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-else-if="!configuration" no-gutters class="ma-6 mt-4">
      <v-col cols="12">
        <v-row no-gutters class="text-center my-12">
          <v-col cols="12">
            <v-icon x-large color="#000">
              mdi-cart-remove
            </v-icon>
          </v-col>
          <v-col cols="12">
            <h4>Produkt-Warenkorb ist leer</h4>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-else no-gutters class="ma-8 mt-4">
      <v-col cols="12" class="text-grey-darken-3">
        <h3>Monatliche Kosten</h3>
      </v-col>
      <v-col cols="12">
        <v-row
          v-for="(charge, index) in monthlyCharges"
          :key="index"
          no-gutters
          class="text-grey-darken-3"
        >
          <v-col
            v-for="(monthly, monthlyKey) in monthlyPrices(charge)"
            :key="monthlyKey"
            class="pl-2"
            cols="12"
            :class="{
              'text-decoration-line-through': isChargeDiscounted(charge.name),
              'text-success font-weight-medium': isChargeDiscount(charge.name),
            }"
          >
            <v-row no-gutters>
              <v-col cols="auto">
                <span v-if="monthly.inMonth === 0 || monthly.inMonth === null">{{
                  charge.name
                }}</span>
                <i v-else class="body-2 pl-4">
                  {{
                    `nach ${
                      monthly.inMonth === 1
                        ? `${monthly.inMonth} Monat`
                        : `${monthly.inMonth} Monaten`
                    }`
                  }}
                </i>
              </v-col>
              <v-col class="text-right ml-auto">
                <h5>
                  {{ `${monthly.price} €` }}
                </h5>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-divider class="mt-4 mb-2" />
        <v-row
          v-for="(monthly, index) in totalMonthly"
          :key="index"
          class="text-grey-darken-3"
          no-gutters
        >
          <v-col cols="auto">
            <h4 v-if="monthly.inMonth === 0" class="text-grey-darken-3">
              Monatliche Gesamtkosten
            </h4>
            <i v-else class="body-2 pl-2">nach {{ monthly.inMonth }} Monaten</i>
          </v-col>
          <v-col class="text-right ml-auto">
            <h5>
              {{ `${monthly.price} €` }}
            </h5>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="mt-6 text-grey-darken-3">
        <h3>Einmalige Kosten</h3>
      </v-col>
      <v-col cols="12" class="pl-4">
        <v-row v-if="!onlyProduct" class="text-grey-darken-3" no-gutters>
          <v-col cols="auto">
            Hausanschlusskosten
          </v-col>
          <v-col class="text-right ml-auto">
            <h5>
              {{ formatPriceWithCurrency(priceList.priceWithProduct) }}
            </h5>
          </v-col>
        </v-row>
        <v-row
          v-for="(charge, index) in onetimeCharges"
          :key="index"
          class="text-grey-darken-3"
          no-gutters
          :class="{
            'text-decoration-line-through': isChargeDiscounted(charge.name),
            'text-success font-weight-medium ': isChargeDiscount(charge.name),
          }"
        >
          <v-col cols="auto">
            {{ charge.name }}
          </v-col>
          <v-col class="text-right ml-auto">
            <h5>
              {{ charge.oneTimePrice ? `${charge.oneTimePrice.price} €` : '0.00 €' }}
            </h5>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-divider class="mt-4 mb-2" />
        <v-row no-gutters>
          <v-col cols="auto">
            <h4 class="text-grey-darken-3">
              Einmalige Gesamtkosten
            </h4>
          </v-col>
          <v-col class="text-right ml-auto text-grey-darken-3">
            <h5>
              {{ formatPriceWithCurrency(+totalOnetime) }}
            </h5>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-alert>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { formatPriceWithCurrency } from '@/composables/formatter';
import type { Article, ConnectionPrice } from '@/types/common';
import type { CartContent, CartItem, MonthlyPrice } from '../../../../types/product-cart';

interface Props {
  articles: Article[];
  configuration?: CartContent;
  priceList: ConnectionPrice;
  onlyProduct?: boolean;
  chartReloading: boolean;
}

const {
  articles,
  priceList,
  onlyProduct = false,
  configuration = undefined,
  chartReloading,
} = defineProps<Props>();

const cartItems = computed<CartItem[]>(() => configuration?.cartItems ?? []);

const freeProducts = computed<CartItem[]>(() => cartItems.value.filter(
  (cartItem: CartItem) => !cartItem.monthlyPrices && !cartItem.oneTimePrice,
));

const monthlyCharges = computed<CartItem[]>(
  () => cartItems.value.filter((cartItem: CartItem) => !!cartItem.monthlyPrices),
);
// The main product (tariff) carries the Bereitstellungspreis as its one-time
// price, which is already listed as its own charge. Exclude the tariff itself
// so its name doesn't show up again under "Einmalige Kosten".
function isMainProduct(cartItem: CartItem): boolean {
  const mainProduct = configuration?.mainProduct;
  if (!mainProduct) {
    return false;
  }
  return mainProduct.articlenumber
    ? cartItem.articleNumber === mainProduct.articlenumber
    : cartItem.name === mainProduct.name;
}

const onetimeCharges = computed<CartItem[]>(() => [
  ...cartItems.value.filter(
    (cartItem: CartItem) => !!cartItem.oneTimePrice && !isMainProduct(cartItem),
  ),
  ...freeProducts.value,
]);

const totalMonthly = computed<MonthlyPrice<string>[]>(
  () => configuration?.summary.monthlyPrices ?? [],
);
const totalOnetime = computed<string | never[]>(
  () => (onetimeCharges.value.length > 0
    ? onetimeCharges.value
      .filter(({ oneTimePrice }) => !!oneTimePrice?.price)
      .map(({ oneTimePrice }) => oneTimePrice!.price)
      .concat([onlyProduct ? 0 : priceList.priceWithProduct])
      .reduce((acc, val) => acc + val, 0)
      .toFixed(2)
    : []),
);

function isChargeDiscount(name: string): boolean {
  // if it does't exist in articles list it means it is a discount article :)
  return !articles.some(({ value }) => value === name);
}

function isChargeDiscounted(name: string): boolean {
  return articles.find(({ value }) => value === name)?.isDiscountApplied || false;
}

function monthlyPrices(cartItem: CartItem) {
  return (cartItem.monthlyPrices ?? [])
    .filter(monthly => !(
      !monthly.price
        && monthly.inMonth === 0
        && (cartItem.monthlyPrices ?? []).some(({ inMonth }) => inMonth === null)
    ))
    .sort((a: MonthlyPrice<number>, b: MonthlyPrice<number>) => a.inMonth - b.inMonth);
}
</script>

<style scoped lang="scss">
  @use '../../assets/sass/main';
</style>
